import {Injectable} from '@angular/core';
import {AuthService, CacheService, PageService} from '@gallaghercp/core-frontend';
import {Router} from '@angular/router';
import {BehaviorSubject, Observable} from 'rxjs';
import {TimeReward} from '@core-app/types/time.reward.type';
import {CompanyPromise} from '@core-app/types/company.promise.type';
import {AddedExtrasReward} from '@core-app/types/added.extras.type';
import {FinancialReward} from '@core-app/types/financial.reward.type';
import {MaximiseReward} from '@core-app/types/maximise.reward.type';
import {ContributionType} from '@core-app/types/contribution.interface';
import {ClientService} from '@core-app/services/client.service';

const STORAGE_KEY: string = 'hide-figures';

@Injectable({
    providedIn: 'root'
})
export class TrsService {

    private readonly hideFigures$: BehaviorSubject<boolean>;

    constructor(
        protected auth: AuthService,
        protected router: Router,
        protected pages: PageService,
        protected cache: CacheService,
        protected client: ClientService,
    ) {
        this.hideFigures$ = new BehaviorSubject<boolean>(this.getFiguresToggleState$FromStorage());
    }

    getUserData(key?: string): string | [] {
        return this.auth.user ? (key ? this.auth.user.data[key] : this.auth.user.data) : [];
    }

    getStatementType(): string {
        return String(this.client.getConfiguration('statementType')).toUpperCase(); // DIGITAL or PRINT
    }

    isPrintRoute(): boolean {
        return this.router.url === '/print';
    }

    getHeroImage(): string {
        return this.client.getConfiguration('heroImage').url
            || 'https://storage.googleapis.com/archipelago-assets/solv-energy/Red%20Mesa%20Solar.jpg';
    }

    getHeroImageAlt(): string {
        return this.client.getConfiguration('heroImage').alt || '';
    }

    getValueStatementVideoId(): string {
        return this.client.getConfiguration('valueStatement').wistiaVideoId;
    }

    getTimeRewards(): TimeReward[] {
        return this.client.getConfiguration('timeRewards') as TimeReward[] || [];
    }

    getCompanyPromise(): CompanyPromise[] {
        return this.client.getConfiguration('companyPromise') as CompanyPromise[] || [];
    }

    getAddedExtrasRewards(): AddedExtrasReward {
        return this.client.getConfiguration('addedExtrasRewards') || {} as AddedExtrasReward;
    }

    getFinancialRewards(): FinancialReward[] {
        return this.client.getConfiguration('financialRewards') as FinancialReward[] || [];
    }

    getMaximiseRewards(): MaximiseReward[] {
        return this.client.getConfiguration('maximiseRewards') as MaximiseReward[] || [];
    }

    // Hides/shows Time Rewards section based on client's config requirements
    isTimeRewardsShownForClient(): boolean {
        return this.getTimeRewards().length > 0;
    }

    isSingleTimeRewardForUser(): boolean {
        return this.getUserTimeRewards().length === 1;
    }

    isVideoShown(): boolean {
        return this.getValueStatementVideoId() ? this.getValueStatementVideoId().length > 0 : false;
    }

    getFiguresToggleState$FromStorage(): boolean {
        return this.cache.getFromStorage(STORAGE_KEY) === 'true';
    }

    setFiguresToggleState(state: boolean): void {
        this.hideFigures$.next(state);
        this.cache.setToStorage(STORAGE_KEY, state);
    }

    getFiguresToggleState$(): Observable<boolean> {
        return this.hideFigures$;
    }

    getCurrencyCode(): string {
        return String(this.client.getConfiguration('currency')).toUpperCase();
    }

    getValueStatementImage(): string {
        return this.client.getConfiguration('valueStatement').profileImage
            || 'https://storage.googleapis.com/archipelago-assets/total-impact-statement/placeholder-hero.jpeg';
    }

    getValueStatementSpeakerName(): string {
        return this.client.getConfiguration('valueStatement').speakerName
            || 'Firstname Lastname';
    }

    getValueStatementSpeakerRole(): string {
        return this.client.getConfiguration('valueStatement').speakerJobRole
            || 'Job Title';
    }

    getValueStatementText(): string {
        return this.client.getConfiguration('valueStatement').text
            || 'Lorem Ipsum';
    }

    getUserTimeRewards(): TimeReward[] {
        return this.getTimeRewards().map((reward: TimeReward, index: number) => {
            reward.value = parseFloat(this.auth.user.data['TIME_REWARD' + reward.rewardIndex + '_VALUE']) || 0;
            reward.unit = this.auth.user.data['TIME_REWARD' + (index + 1) + '_UNIT'] || reward.unit;

            return reward;
        }).filter((reward: TimeReward) => {
            return reward.value > 0;
        });
    }

    getUserFinancialRewards(): FinancialReward[] {
        return this.getFinancialRewards().map((reward: FinancialReward) => {
            reward.value = parseFloat(this.auth.user.data['FINANCIAL_REWARD' + reward.rewardIndex]) || 0;
            return reward;
        }).filter((reward: FinancialReward) => {
            return reward.value > 0;
        });
    }

    getUserMaximiseRewards(): MaximiseReward[] {
        return this.getMaximiseRewards().filter((reward: MaximiseReward) => {
            if (!reward.logic) {
                return false;
            }

            if (reward.logic === 'ALL') {
                return true;
            }

                       const parts = reward.logic.split('|');
                       const operator = parts[1];
                       let x = parts[0];
                       let y = parts[2];
            if (!x || !operator || !y) {
                return false;
            }

            [x, y] = [x, y].map((item: any) => {
                if (item.indexOf('rewardIndex:') > -1) {
                    item = parseFloat(this.auth.user.data['FINANCIAL_REWARD' + item.split(':').splice(-1, 1)]) || 0;
                } else if (item.indexOf('value') > -1) {
                    item = parseFloat(this.auth.user.data['CTA' + reward.ctaIndex + '_VALUE']) || 0;
                } else {
                    item = parseFloat(item);
                }
                return item;
            });

            switch (operator) {
                case '>':
                    return x > y;
                case '<':
                    return x < y;
                case '=':
                    return x === y;
            }

            return false;
        });
    }

    // If > 6 we have a second Financial Rewards page for PDF
    getUserFinancialRewardsLengthForPdf(): number {
        return this.getUserFinancialRewards().length;
    }

    // If > 6 we have a second Added Extras page for PDF
    getUserAddedExtrasLengthForPdf(): number {
        return (this.getAddedExtrasRewards().categoryRewards || []).length + (this.isSingleTimeRewardForUser() ? 1 : 0);
    }

    isFinancialRewardPageTwoNeededForPdf(): boolean {
        return this.getUserFinancialRewardsLengthForPdf() > 6;
    }

    isAddedExtrasPageTwoNeededForPdf(): boolean {
        return this.getUserAddedExtrasLengthForPdf() > 6;
    }

    showPdfBlankPage1(): boolean {
        return (!this.isFinancialRewardPageTwoNeededForPdf()
                && this.isAddedExtrasPageTwoNeededForPdf()
                && this.isTimeRewardsShownForClient())
            || (this.isFinancialRewardPageTwoNeededForPdf()
                && !this.isAddedExtrasPageTwoNeededForPdf()
                && this.isTimeRewardsShownForClient())
            || (this.isFinancialRewardPageTwoNeededForPdf()
                && this.isAddedExtrasPageTwoNeededForPdf()
                && !this.isTimeRewardsShownForClient())
            || (!this.isFinancialRewardPageTwoNeededForPdf()
                && !this.isAddedExtrasPageTwoNeededForPdf()
                && !this.isTimeRewardsShownForClient());
    }

    getPlanTitle(plan: ContributionType): string {

        // According to the data spec, the string value associated
        // will be "Allstate ID Theft Employee", but need to
        // present as "ID Theft Employee - Allstate"
        if (plan.title === "ID_THEFT_PROTECTION_LABEL") {
            const titleWithoutAllstate = this.auth.user.data[plan.title].split("Allstate")[1].trim();
            return `${titleWithoutAllstate} - Allstate`;
        }

        let title = this.auth.user.data[plan.title] || plan.title;

        if (plan.prefix) {
            title = `${plan.prefix} - ` + title;
        }

        if (plan.suffix) {
            title = title + ` - ${plan.suffix}`;
        }

        return title;
    }

}
